import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { useDispatch } from "react-redux";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    FormControl,
    InputLabel,
    Tooltip,
    TableFooter,
    TablePagination,
    Checkbox,
    ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/system";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import styled from "styled-components";
import * as groupService from "../services/groupService";
import * as usinaService from "../services/usinaService";
import { HeaderTitle } from "../styles/UsuariosStyle";
import { Container } from "react-bootstrap";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#0a5068",
        color: "#fff",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: "#e9ecef",
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

function TablePaginationActions(props) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
                style={{ marginTop: -15 }}
            >
                {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
                style={{ marginTop: -15 }}
            >
                {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
                style={{ marginTop: -15 }}
            >
                {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
                style={{ marginTop: -15, marginRight: 8 }}
            >
                {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    );
}

const defaultGroup = {
    name: "",
    powerPlants: [],
};

const defaultFilters = {
    nome: "",
};

export default function GroupsPage() {
    const dispatch = useDispatch();
    const [group, setGroup] = useState(defaultGroup);
    const [isOpenAddGroup, setIsOpenAddGroup] = useState(false);
    const [isOpenDeleteGroup, setIsOpenDeleteGroup] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isVisualize, setIsVisualize] = useState(false);
    const [loading, setLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filters, setFilters] = useState(defaultFilters);
    const [usinas, setUsinas] = useState([]);
    const [loadingUsinas, setLoadingUsinas] = useState(false);

    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        fetchGroups();
    }, [page, rowsPerPage]);

    useEffect(() => {
        loadUsinas();
    }, []);

    const loadUsinas = async () => {
        try {
            setLoadingUsinas(true);
            const response = await usinaService.getAllUsinas();
            setUsinas(response);
        } catch (error) {
            enqueueSnackbar("Ocorreu um erro ao buscar as usinas!", { variant: "error" });
        } finally {
            setLoadingUsinas(false);
        }
    };

    const fetchGroups = async (limpar = false) => {
        try {
            setLoading(true);
            let list = await groupService.getAllGroups();
            if (!limpar && filters.nome) {
                list = list.filter((g) =>
                    g.name.toLowerCase().includes(filters.nome.toLowerCase())
                );
            }
            setTotal(list.length);
            const start = page * rowsPerPage;
            setGroups(list.slice(start, start + rowsPerPage));
        } catch (error) {
            enqueueSnackbar("Ocorreu um erro ao buscar grupos!", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const getGroup = async (groupId) => {
        try {
            const response = await groupService.getOneGroup(groupId);
            const plants = response.powerPlants
                ? response.powerPlants.map((p) => (typeof p === "object" ? p.id : p))
                : [];
            setGroup({ ...response, powerPlants: plants });
        } catch (error) {
            enqueueSnackbar("Erro ao buscar grupo", { variant: "error" });
        }
    };

    const handleOpenAddGroup = () => {
        setGroup(defaultGroup);
        setIsOpenAddGroup(true);
    };

    const handleCloseAddGroup = () => {
        setIsOpenAddGroup(false);
        setIsEdit(false);
        setIsVisualize(false);
        setGroup(defaultGroup);
    };

    const handleOpenDeleteGroup = (selectedGroup) => {
        setGroup(selectedGroup);
        setIsOpenDeleteGroup(true);
    };

    const handleOpenEditGroup = async (groupId) => {
        setIsEdit(true);
        await getGroup(groupId);
        setIsOpenAddGroup(true);
    };

    const handleOpenVisualizeGroup = async (selectedGroup) => {
        await getGroup(selectedGroup.id);
        setIsVisualize(true);
        setIsOpenAddGroup(true);
    };

    const handleAddGroup = async () => {
        try {
            setLoading(true);
            await groupService.addGroup(group);
            enqueueSnackbar("Grupo cadastrado com sucesso!", { variant: "success" });
            handleCloseAddGroup();
            fetchGroups(true);
        } catch (error) {
            enqueueSnackbar("Erro ao cadastrar grupo", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleEditGroup = async () => {
        try {
            setLoading(true);
            await groupService.editGroup({ groupId: group.id, group });
            enqueueSnackbar("Grupo atualizado com sucesso!", { variant: "success" });
            handleCloseAddGroup();
            fetchGroups();
        } catch (error) {
            enqueueSnackbar("Erro ao atualizar grupo", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteGroup = async () => {
        try {
            setLoading(true);
            await groupService.deleteGroup(group.id);
            enqueueSnackbar("Grupo excluído com sucesso!", { variant: "success" });
            setIsOpenDeleteGroup(false);
            fetchGroups();
        } catch (error) {
            enqueueSnackbar("Erro ao excluir grupo", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <Paper style={{ padding: 16 }}>
                <Container
                    className="pt-3"
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                        }}
                    >
                        <HeaderTitle>Grupos</HeaderTitle>
                        <Button
                            variant="contained"
                            startIcon={<GroupAddIcon />}
                            style={{ backgroundColor: "#0a5068", color: "white" }}
                            onClick={handleOpenAddGroup}
                        >
                            Novo grupo
                        </Button>
                    </Box>

                    <Box sx={{ mb: 3, mt: 4 }}>
                        <HeaderTitle style={{ fontSize: 16 }}>Filtros</HeaderTitle>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                        <TextField
                            label="Nome"
                            variant="outlined"
                            size="small"
                            value={filters.nome}
                            onChange={(e) => setFilters({ ...filters, nome: e.target.value })}
                            sx={{ mr: 2 }}
                        />
                        <Button
                            onClick={() => {
                                setFilters(defaultFilters);
                                fetchGroups(true);
                            }}
                            size="large"
                            style={{ color: "#0a5068", borderColor: "#0a5068" }}
                        >
                            Limpar
                        </Button>
                        <Button
                            onClick={() => {
                                fetchGroups();
                            }}
                            size="large"
                            style={{ backgroundColor: "#0a5068", color: "white", ml: 2 }}
                        >
                            Buscar
                        </Button>
                    </Box>

                    {!loading ? (
                        <Container>
                            <TableContainer sx={{ maxHeight: 460, marginTop: 6 }} component={Paper}>
                                <Table stickyHeader sx={{ minWidth: 460 }} aria-label="customized table">
                                    <TableHead>
                                        <TableRow style={{ height: 1 }}>
                                            <StyledTableCell>Nome</StyledTableCell>
                                            <StyledTableCell align="center">Usinas</StyledTableCell>
                                            <StyledTableCell className="text-end">Ações</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {groups.map((grp) => (
                                            <StyledTableRow key={grp.id}>
                                                <StyledTableCell component="th" scope="row">
                                                    {grp.name}
                                                </StyledTableCell>
                                                <StyledTableCell align="center">
                                                    {grp.powerPlants ? grp.powerPlants.length : 0}
                                                </StyledTableCell>
                                                <StyledTableCell align="right">
                                                    <Tooltip title="Visualizar" arrow>
                                                        <IconButton
                                                            onClick={() => handleOpenVisualizeGroup(grp)}
                                                            size="large"
                                                        >
                                                            <RemoveRedEyeIcon
                                                                style={{ fill: "#0a5068" }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Editar" arrow>
                                                        <IconButton
                                                            size="large"
                                                            onClick={() => handleOpenEditGroup(grp.id)}
                                                        >
                                                            <EditIcon style={{ fill: "#0a5068" }} />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Remover" arrow>
                                                        <IconButton
                                                            onClick={() => handleOpenDeleteGroup(grp)}
                                                            size="large"
                                                        >
                                                            <DeleteIcon
                                                                style={{ fill: "#0a5068" }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ textAlign: "center" }}>
                                                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                                    <TablePagination
                                                        rowsPerPageOptions={[5, 10, 25]}
                                                        colSpan={3}
                                                        count={total}
                                                        rowsPerPage={rowsPerPage}
                                                        page={page}
                                                        SelectProps={{
                                                            inputProps: {
                                                                "aria-label": "rows per page",
                                                            },
                                                            native: true,
                                                        }}
                                                        onPageChange={handleChangePage}
                                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                                        ActionsComponent={TablePaginationActions}
                                                    />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>
                        </Container>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "50vh",
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                </Container>
            </Paper>

            {/* add/edit dialog */}
            <Dialog
                maxWidth="sm"
                fullWidth
                disableScrollLock={true}
                open={isOpenAddGroup}
                onClose={handleCloseAddGroup}
            >
                <DialogTitle style={{ color: "#0a5068" }}>
                    {isEdit
                        ? "Editar grupo"
                        : isVisualize
                            ? "Visualizar grupo"
                            : "Criar novo grupo"}
                </DialogTitle>
                <DialogContent>
                    <Divider
                        variant="middle"
                        sx={{ mb: 3, color: "#0a5068", padding: "0.5px" }}
                    />
                    <Box
                        noValidate
                        component="form"
                        sx={{ display: "flex", flexDirection: "column" }}
                    >
                        <TextField
                            label="Nome"
                            sx={{ m: 1, width: "90%" }}
                            variant="standard"
                            disabled={isVisualize}
                            value={group.name}
                            onChange={(evt) =>
                                setGroup({ ...group, name: evt.target.value })
                            }
                        />

                        <FormControl sx={{ m: 1, width: "90%" }}>
                            <InputLabel>Usinas</InputLabel>
                            <Select
                                multiple
                                value={group.powerPlants}
                                disabled={isVisualize || loadingUsinas}
                                variant="standard"
                                onChange={(e) =>
                                    setGroup({
                                        ...group,
                                        powerPlants: e.target.value,
                                    })
                                }
                                label="Usinas"
                                renderValue={(selected) =>
                                    selected
                                        .map((id) => {
                                            const u = usinas.find((x) => x.id === id);
                                            return u ? u.nome : id;
                                        })
                                        .join(", ")
                                }
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}
                            >
                                {usinas.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        <Checkbox
                                            checked={
                                                group.powerPlants.indexOf(u.id) > -1
                                            }
                                        />
                                        <ListItemText primary={u.nome} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddGroup}>Fechar</Button>
                    {!isVisualize && (
                        <Button onClick={isEdit ? handleEditGroup : handleAddGroup}>
                            Confirmar
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* delete confirmation */}
            <Dialog
                maxWidth="sm"
                fullWidth
                disableScrollLock={true}
                open={isOpenDeleteGroup}
                onClose={() => setIsOpenDeleteGroup(false)}
            >
                <DialogTitle style={{ color: "#0a5068" }}>
                    Remover grupo
                </DialogTitle>
                <DialogContent>
                    <Divider
                        variant="middle"
                        sx={{ mb: 4, color: "#0a5068", padding: "0.5px" }}
                    />
                    Você tem certeza que deseja remover o grupo {group.name}?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsOpenDeleteGroup(false)}>
                        Fechar
                    </Button>
                    <Button onClick={handleDeleteGroup}>Confirmar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}